import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  InjectionToken,
  Optional,
  Inject,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from '../../i18n.service';
import { TranslateService } from '../../services/translate.service';

/**
 * Opt-in token for the {@link AuthLanguageSwitcherComponent}.
 *
 * Provide an array of language codes (e.g. `['en', 'de']`) at the app root
 * to render the switcher on the login/register/forgot-password/reset-password
 * pages. Apps that do not provide this token render nothing — preserving
 * backwards-compatible behaviour for MNLO, Credo, fac3less and nam3less.
 */
export const AUTH_LANGUAGE_SWITCHER_LANGS = new InjectionToken<string[]>(
  'AUTH_LANGUAGE_SWITCHER_LANGS'
);

@Component({
  selector: 'lib-auth-language-switcher',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './auth-language-switcher.component.html',
  styleUrl: './auth-language-switcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthLanguageSwitcherComponent {
  private i18nService = inject(I18nService);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  readonly langs: string[];
  readonly currentLang = signal<string>(this.i18nService.getLanguage());

  constructor(
    @Optional()
    @Inject(AUTH_LANGUAGE_SWITCHER_LANGS)
    configured: string[] | null
  ) {
    this.langs = configured ?? [];
    this.translateService.langChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang) => this.currentLang.set(lang));
  }

  switch(lang: string): void {
    // Stay on the current URL — the auth pages are not language-prefixed.
    this.i18nService.setLanguage(lang, false);
  }
}
